class YouMayAlsoLike extends HTMLElement {
  /**
   * Lazy-load when the element is near the viewport
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#waitForCoordinateWith();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {AbortController | null} */
  #activeFetch = null;
  #loaded = false;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
  }

  /**
   * Wait for the "Coordinate with" section to finish loading so we can
   * read its product handles for deduplication. Falls back after 3 seconds
   * or proceeds immediately if no Coordinate with section exists.
   */
  #waitForCoordinateWith() {
    const coordEl = document.querySelector('product-recommendations');

    if (!coordEl) {
      this.#loadSection();
      return;
    }

    if (coordEl.dataset.recommendationsPerformed === 'true') {
      this.#loadSection();
      return;
    }

    let resolved = false;
    const resolve = () => {
      if (resolved) return;
      resolved = true;
      mutationObserver.disconnect();
      clearTimeout(timeout);
      this.#loadSection();
    };

    const timeout = setTimeout(resolve, 3000);

    const mutationObserver = new MutationObserver(() => {
      if (
        document.querySelector(
          'product-recommendations[data-recommendations-performed="true"]'
        )
      ) {
        resolve();
      }
    });

    mutationObserver.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ['data-recommendations-performed'],
    });
  }

  /**
   * Read the product handles that are currently shown in the
   * "Coordinate with" (product-recommendations) section.
   * @returns {Set<string>}
   */
  #getCoordinateHandles() {
    const coordSection = document.querySelector('product-recommendations');
    if (!coordSection) return new Set();

    const handles = new Set();
    coordSection.querySelectorAll('.resource-list__item').forEach((card) => {
      const link = card.querySelector('a[href*="/products/"]');
      if (link) {
        const match = link.href.match(/\/products\/([^?#\/]+)/);
        if (match) handles.add(match[1]);
      }
    });
    return handles;
  }

  /**
   * Fetch the section HTML via the Section Rendering API, deduplicate
   * in memory, and insert the cleaned result into the DOM.
   */
  async #loadSection() {
    if (this.#loaded) return;
    this.#loaded = true;

    const { productHandle, sectionId } = this.dataset;
    const id = this.id;

    if (!productHandle || !sectionId || !id) return;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const url = `${window.Shopify.routes.root}products/${productHandle}?section_id=${sectionId}`;
      const response = await fetch(url, { signal: this.#activeFetch.signal });

      if (!response.ok) {
        this.#handleError(new Error(`Server returned ${response.status}`));
        return;
      }

      const text = await response.text();

      const temp = document.createElement('div');
      temp.innerHTML = text;
      const newSection = temp.querySelector(`you-may-also-like[id="${id}"]`);

      if (!newSection?.innerHTML?.trim()) {
        this.#handleError(new Error('No suggestions available'));
        return;
      }

      const coordHandles = this.#getCoordinateHandles();
      const maxProducts = parseInt(this.dataset.maxProducts) || 4;

      if (coordHandles.size > 0) {
        this.#deduplicateInMemory(newSection, coordHandles, maxProducts);
      } else {
        this.#trimToMax(newSection, maxProducts);
      }

      const remaining = newSection.querySelectorAll(
        '.resource-list__item[data-product-handle]'
      );
      if (remaining.length === 0) {
        this.classList.add('hidden');
        return;
      }

      this.innerHTML = newSection.innerHTML;
      this.dataset.ymalLoaded = 'true';

      // Trim to a full multiple of visibleSlides.length so the last carousel
      // page is never partially empty. Runs after a rAF so the Slideshow
      // component has completed its connectedCallback and getVisibleElements
      // can read accurate layout ??? giving the real per-breakpoint page size
      // rather than the configured --column-count CSS variable.
      await this.#trimToVisibleMultiple();
    } catch (e) {
      if (e.name !== 'AbortError') {
        this.#handleError(e);
      }
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Remove products that appear in "Coordinate with" and trim to
   * maxProducts. Works on the in-memory DOM before it is inserted,
   * so there is never a flash of incorrect content.
   */
  #deduplicateInMemory(container, coordHandles, maxProducts) {
    container.querySelectorAll('.resource-list').forEach((list) => {
      const items = Array.from(
        list.querySelectorAll('.resource-list__item[data-product-handle]')
      );
      let kept = 0;

      items.forEach((item) => {
        const handle = item.getAttribute('data-product-handle');
        if (coordHandles.has(handle) || kept >= maxProducts) {
          // Remove the slideshow-slide wrapper so no empty slide shell
          // remains to inflate the Slideshow component's slides count.
          (item.closest('slideshow-slide') ?? item).remove();
        } else {
          kept++;
        }
      });
    });
  }

  /**
   * When there is nothing to deduplicate, just cap at maxProducts.
   */
  #trimToMax(container, maxProducts) {
    container.querySelectorAll('.resource-list').forEach((list) => {
      const items = Array.from(
        list.querySelectorAll('.resource-list__item[data-product-handle]')
      );
      items.forEach((item, i) => {
        if (i >= maxProducts) (item.closest('slideshow-slide') ?? item).remove();
      });
    });
  }

  /**
   * Trim the live DOM so slides.length is a multiple of visibleSlides.length,
   * ensuring the last carousel page is always full with no blank slots and
   * the Slideshow's next-button disabled logic fires at the right position.
   *
   * Runs after a requestAnimationFrame so the Slideshow component has
   * completed its connectedCallback and getVisibleElements() can read
   * accurate layout. This gives the real per-breakpoint page size rather
   * than the configured --column-count CSS variable, which only reflects
   * the desktop column setting and may differ at responsive breakpoints.
   */
  async #trimToVisibleMultiple() {
    await new Promise((resolve) => requestAnimationFrame(resolve));

    const slideshow = this.querySelector('slideshow-component');
    if (!slideshow) return;

    const visibleCount = slideshow.visibleSlides?.length;
    if (!visibleCount || visibleCount <= 1) return;

    const slides = slideshow.slides;
    if (!slides?.length) return;

    const remainder = slides.length % visibleCount;
    if (remainder === 0) return;

    for (const slide of slides.slice(slides.length - remainder)) {
      slide.remove();
    }
  }

  /** @param {Error} error */
  #handleError(error) {
    console.error('You may also like error:', error);
    this.dataset.ymalLoaded = 'true';
  }
}

if (!customElements.get('you-may-also-like')) {
  customElements.define('you-may-also-like', YouMayAlsoLike);
}
